import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import messages from './local/index';

const LANGUAGE_STORAGE_KEY = 'alsiyahiah.language';
const supportedLanguages = ['ar', 'en'] as const;
let activeLanguage = 'ar';
let directionObserver: MutationObserver | null = null;
let directionSyncScheduled = false;

function getInitialLanguage() {
  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return supportedLanguages.includes(saved as (typeof supportedLanguages)[number]) ? saved : 'ar';
}

function applyDocumentLanguage(language: string) {
  activeLanguage = language;
  const isEnglish = language === 'en';
  const direction = isEnglish ? 'ltr' : 'rtl';
  document.documentElement.dir = direction;
  document.documentElement.lang = isEnglish ? 'en' : 'ar';
  document.body?.setAttribute('dir', direction);
  syncRenderedDirection(direction);
  ensureDirectionObserver();
}

function syncRenderedDirection(direction: string) {
  document.querySelectorAll<HTMLElement>('#root [dir="rtl"], #root [dir="ltr"]').forEach((element) => {
    if (element.dataset.preserveDir === 'true') return;
    if (element.getAttribute('dir') !== direction) {
      element.setAttribute('dir', direction);
    }
  });
}

function ensureDirectionObserver() {
  if (directionObserver) return;

  const root = document.getElementById('root');
  if (!root) return;

  directionObserver = new MutationObserver(() => {
    if (directionSyncScheduled) return;

    directionSyncScheduled = true;
    window.requestAnimationFrame(() => {
      directionSyncScheduled = false;
      syncRenderedDirection(activeLanguage === 'en' ? 'ltr' : 'rtl');
    });
  });

  directionObserver.observe(root, {
    attributes: true,
    attributeFilter: ['dir'],
    childList: true,
    subtree: true,
  });
}

i18n
  .use(initReactI18next)
  .init({
    lng: getInitialLanguage(),
    fallbackLng: 'ar',
    supportedLngs: [...supportedLanguages],
    debug: false,
    resources: messages,
    interpolation: {
      escapeValue: false,
    },
  });

applyDocumentLanguage(i18n.language);

i18n.on('languageChanged', (language) => {
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  applyDocumentLanguage(language);
});

export default i18n;
